import {COLOR_PALETTES, isDiscreteColorPalette} from 'analysis-tool/ui/charting/ChartingConstants';
import {DiscreteColorPalette} from 'analysis-tool/ui/charting/ChartingTypes';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {assertJSONObject, isJSONArray, JSONObject, JSONValue} from 'util/Types';

import {
  BANNER_KEY,
  CONDITIONAL_UI_SCHEMA_KEY,
  ConfigDataFrameInfo,
  ConfigShapePath,
  DisableFunction,
  EXPANDABLE_DETAILS_KEY,
  FIELD_DEFAULTS_GENERATOR_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchema,
  SHOW_DIVIDER_LINE_KEY,
  StepConfigEditorOverride,
} from '../../StepConfigEditorTypes';
import {isSeriesColumnNameNull} from '../StepConfigDisableFieldFunctions';
import {maybeGetValueFromConfig} from '../StepConfigSettingsUtils';

/**
 * Creates a conditional UI schema section for a specific chart type.
 *
 * This helper wraps chart-specific configuration or style sections with the
 * CONDITIONAL_UI_SCHEMA_KEY to ensure they only render when the matching
 * chart type is selected.
 *
 * The section order is determined by the FIELD_ORDER_UI_SCHEMA_KEY defined in the sections object.
 *
 * @param chartType - The chart type identifier (e.g., 'BOX_PLOT', 'PIE_CHART')
 * @param sections - The sections to include in the schema (must include FIELD_ORDER_UI_SCHEMA_KEY)
 * @returns A ShapeUISchema with conditional rendering based on chart type
 *
 * @example
 * const boxPlotConfig = createChartTypeSchema('BOX_PLOT', BOX_PLOT_CONFIG_SECTIONS);
 */
export function createChartTypeSchema<T extends {[FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>}>(
  chartType: string,
  sections: T
): ShapeUISchema {
  return {
    [CONDITIONAL_UI_SCHEMA_KEY]: {
      tagValue: chartType,
      configShapePath: ['chartConfig', 'type'],
    },
    ...sections,
  } as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// Shared color palette constants
// ---------------------------------------------------------------------------

export const DISCRETE_COLOR_PALETTE_DISPLAY_NAMES = {
  BENCHLING: 'Benchling (30)',
  DARK24: 'Dark (24)',
  LIGHT24: 'Light (24)',
  BOLD: 'Bold (11)',
  PASTEL: 'Pastel (11)',
  SAFE: 'Safe (11)',
  // Not named "Prism" for display to avoid confusion with Prism the company
  PRISM: 'Crystal (11)',
} as const;

export const DISCRETE_COLOR_PALETTE_SVG_PATHS = {
  BENCHLING: '/static/img/insights/palettes/benchling.svg',
  DARK24: '/static/img/insights/palettes/dark24.svg',
  LIGHT24: '/static/img/insights/palettes/light24.svg',
  BOLD: '/static/img/insights/palettes/bold.svg',
  PASTEL: '/static/img/insights/palettes/pastel.svg',
  SAFE: '/static/img/insights/palettes/safe.svg',
  PRISM: '/static/img/insights/palettes/prism.svg',
} as const;

export const GRADIENT_COLOR_PALETTE_DISPLAY_NAMES = {
  VIRIDIS: 'Viridis',
  PLASMA: 'Plasma',
  BLUERED: 'Blue-Red',
  HOT: 'Hot',
  BLUES: 'Blues',
  REDS: 'Reds',
} as const;

export const GRADIENT_COLOR_PALETTE_SVG_PATHS = {
  VIRIDIS: '/static/img/insights/palettes/viridis.png',
  PLASMA: '/static/img/insights/palettes/plasma.png',
  BLUERED: '/static/img/insights/palettes/bluered.png',
  HOT: '/static/img/insights/palettes/hot.png',
  BLUES: '/static/img/insights/palettes/blues.png',
  REDS: '/static/img/insights/palettes/reds.png',
} as const;

// ---------------------------------------------------------------------------
// Shared middleware & defaults for discrete color palettes
// ---------------------------------------------------------------------------

/**
 * Recolors seriesColorConfigs when the selected palette is a discrete palette.
 * Shared by both the discrete-only palette middleware and the unified scatter palette middleware.
 */
export function recolorSeriesForPalette(
  paletteName: string,
  oldConfig: Record<string, JSONValue>
): Record<string, JSONValue> | null {
  const resolvedPalette: DiscreteColorPalette = isDiscreteColorPalette(paletteName)
    ? paletteName
    : 'BENCHLING';
  const palette = COLOR_PALETTES[resolvedPalette];

  const chartConfig = oldConfig?.chartConfig;
  assertJSONObject(chartConfig);
  const parameters = chartConfig.parameters;
  assertJSONObject(parameters);
  const seriesColorConfigs = parameters.seriesColorConfigs;

  if (!isJSONArray(seriesColorConfigs) || seriesColorConfigs.length === 0) {
    return null;
  }

  const newSeriesColorConfigs = seriesColorConfigs.map(
    (mapping: JSONObject, idx: number): JSONObject => ({
      ...mapping,
      color: palette[idx % palette.length],
    })
  );

  return {
    ...oldConfig,
    chartConfig: {
      ...chartConfig,
      parameters: {
        ...parameters,
        seriesColorConfigs: newSeriesColorConfigs,
      },
    },
  };
}

/**
 * Middleware that regenerates series colors when a color palette field changes.
 *
 * Handles both the discrete-only `discreteColorPalette` field (bar, boxplot, line, pie)
 * and the unified `colorPalette` field (scatter). Gradient palette values are ignored
 * since series recoloring only applies to discrete palettes.
 */
export function onChangeColorPaletteMiddleware(
  newValue: string,
  path: ConfigShapePath,
  oldConfig: Record<string, JSONValue>,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
): Record<string, JSONValue> {
  const field = path[2];
  const isChangingPalette =
    path.length === 3 &&
    path[0] === 'chartConfig' &&
    path[1] === 'parameters' &&
    (field === 'colorPalette' || field === 'discreteColorPalette');

  if (!isChangingPalette || !isDiscreteColorPalette(newValue)) {
    return oldConfig;
  }

  return recolorSeriesForPalette(newValue, oldConfig) ?? oldConfig;
}

/**
 * Generates a default color for a chart series based on the current palette and index.
 */
export function getDefaultSeriesColorConfig(
  config: JSONObject,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  idx = 0
): JSONObject {
  const chartConfig = config.chartConfig;
  assertJSONObject(chartConfig);

  const parameters = chartConfig.parameters;
  assertJSONObject(parameters);

  const rawPalette = parameters?.colorPalette ?? parameters?.discreteColorPalette;
  const discreteColorPalette: DiscreteColorPalette = isDiscreteColorPalette(rawPalette)
    ? rawPalette
    : 'BENCHLING';
  const paletteHexColors = COLOR_PALETTES[discreteColorPalette];
  return {
    color: paletteHexColors[idx % paletteHexColors.length],
  };
}

// ---------------------------------------------------------------------------
// 1a. Discrete color palette section factory
// ---------------------------------------------------------------------------

/**
 * Creates the color palette + per-series color override section used by charts
 * with discrete color palettes (Bar, BoxPlot, Line, Pie).
 *
 * @param mappingSourcePath - config path to the column that drives per-series mapping
 *   (e.g. `['chartConfig', 'parameters', 'seriesColumnName']` or `sliceColumnName` for Pie)
 * @param options.defaultValue - optional default palette name (e.g. 'BENCHLING')
 */
export function createDiscreteColorPaletteSection(
  mappingSourcePath: ConfigShapePath,
  options?: {defaultValue?: DiscreteColorPalette}
): ShapeUISchema {
  return {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['colorConfigSection', 'seriesColorConfigsSection'],
    [EXPANDABLE_DETAILS_KEY]: {
      headerField: 'colorConfigSection',
      detailFields: ['seriesColorConfigsSection'],
    },
    [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]: onChangeColorPaletteMiddleware,
    colorConfigSection: {
      displayName: 'Color palette',
      configShapePath: ['chartConfig', 'parameters', 'discreteColorPalette'],
      fieldSettings: {
        defaultValue: options?.defaultValue ?? 'BENCHLING',
        displayNames: DISCRETE_COLOR_PALETTE_DISPLAY_NAMES,
        svgImagePaths: DISCRETE_COLOR_PALETTE_SVG_PATHS,
      },
    },
    seriesColorConfigsSection: {
      configShapePath: ['chartConfig', 'parameters', 'seriesColorConfigs'],
      editorOverride: StepConfigEditorOverride.MAPPING_ARRAY,
      fieldSettings: {
        mappingSourcePath,
        mappingKey: 'series',
        showNoMappingEditor: true,
        noMappingDisplayName: 'Base color',
        noMappingSentinel: '__BENCHLING_NO_SERIES_DEFAULT__',
      },
      elementUISchema: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: ['series', 'color'],
        [FIELD_DEFAULTS_GENERATOR_KEY]: getDefaultSeriesColorConfig,
        series: {
          configShapePath: ['series'],
          disableFn: (): boolean => true,
          editorOverride: StepConfigEditorOverride.STRING_LABEL,
        },
        color: {
          configShapePath: ['color'],
          editorOverride: StepConfigEditorOverride.COLOR_PICKER,
        },
      },
    },
  } as unknown as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// 1b. Legend style section factory
// ---------------------------------------------------------------------------

/**
 * Creates the legend section used in chart Style tabs.
 *
 * @param options.infoText - optional info banner text (e.g. "A series column must be selected...")
 * @param options.disableFn - optional function to disable fields (e.g. isSeriesColumnNameNull)
 * @param options.showLegendDefault - default value for the showLegend checkbox
 */
export function createLegendStyleSection(options?: {
  infoText?: string | false;
  disableFn?: DisableFunction | false;
  showLegendDefault?: boolean;
}): ShapeUISchema {
  const infoText =
    options?.infoText === false
      ? undefined
      : (options?.infoText ?? 'A series column must be selected to show a legend.');
  const disableFn = options?.disableFn === false ? undefined : (options?.disableFn ?? isSeriesColumnNameNull);
  return {
    [SECTION_NAME_UI_SCHEMA_KEY]: 'Legend',
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['showLegend', 'position', 'customLabel'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    ...(infoText != null ? {[INFO_TEXT_UI_SCHEMA_KEY]: infoText} : {}),
    showLegend: {
      configShapePath: ['chartConfig', 'parameters', 'showLegend'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Show legend',
        defaultValue: options?.showLegendDefault ?? true,
      },
      ...(disableFn != null ? {disableFn} : {}),
    },
    position: {
      displayName: 'Position',
      configShapePath: ['chartConfig', 'parameters', 'legendLocation'],
      fieldSettings: {
        displayNames: {
          TOP: 'Top',
          LEFT: 'Left',
          RIGHT: 'Right',
        },
      },
      ...(disableFn != null ? {disableFn} : {}),
    },
    customLabel: {
      displayName: 'Custom label (optional)',
      configShapePath: ['chartConfig', 'parameters', 'legendLabel'],
      ...(disableFn != null ? {disableFn} : {}),
    },
  } as unknown as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// 1c. Marker style section factory
// ---------------------------------------------------------------------------

const MARKER_SHAPE_DISPLAY_NAMES = {
  CIRCLE: 'Circle',
  DIAMOND: 'Diamond',
  SQUARE: 'Square',
  CROSS: 'Cross',
} as const;

const MARKER_SHAPE_ICON_NAMES = {
  CIRCLE: 'circle',
  DIAMOND: 'diamond',
  SQUARE: 'square',
  CROSS: 'plus',
} as const;

/**
 * Creates the marker/point style section used in chart Style tabs.
 *
 * @param options.sectionName - display name for the section (default: 'Marker styling')
 * @param options.shapeFieldName - display name for the shape field (default: 'Marker shape')
 * @param options.allowNullShape - whether to allow a "None" option for marker shape (Line chart)
 */
export function createMarkerStyleSection(options?: {
  sectionName?: string;
  shapeFieldName?: string;
  allowNullShape?: boolean;
}): ShapeUISchema {
  const sectionName = options?.sectionName ?? 'Marker styling';
  const shapeFieldName = options?.shapeFieldName ?? 'Marker shape';

  return {
    [SECTION_NAME_UI_SCHEMA_KEY]: sectionName,
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['markerShape', 'markerOpacity', 'outlineOnly'],
    [SHOW_DIVIDER_LINE_KEY]: true,
    [EXPANDABLE_DETAILS_KEY]: {
      headerField: 'markerShape',
      detailFields: ['markerOpacity', 'outlineOnly'],
    },
    markerShape: {
      displayName: shapeFieldName,
      configShapePath: ['chartConfig', 'parameters', 'markerShape'],
      fieldSettings: {
        displayNames: MARKER_SHAPE_DISPLAY_NAMES,
        iconNames: MARKER_SHAPE_ICON_NAMES,
        ...(options?.allowNullShape ? {allowNull: true, nullIconName: 'ban', nullDisplayName: 'None'} : {}),
      },
      editorOverride: StepConfigEditorOverride.ICON_SELECTOR,
    },
    markerOpacity: {
      displayName: 'Opacity',
      configShapePath: ['chartConfig', 'parameters', 'markerOpacity'],
    },
    outlineOnly: {
      displayName: 'Outline only',
      configShapePath: ['chartConfig', 'parameters', 'markerShapeOpen'],
      editorOverride: StepConfigEditorOverride.CHECKBOX,
      fieldSettings: {
        checkboxLabel: 'Outline only',
      },
    },
  } as unknown as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// 1d. Axis range style section factory
// ---------------------------------------------------------------------------

export const axisMinMaxBannerVisibilityFn = (config: Record<string, JSONValue>): boolean => {
  const yAxisIncludesZero = maybeGetValueFromConfig(config, [
    'chartConfig',
    'parameters',
    'yAxisIncludesZero',
  ]);
  const yAxisMin = maybeGetValueFromConfig(config, ['chartConfig', 'parameters', 'yAxisMin']);
  const yAxisMax = maybeGetValueFromConfig(config, ['chartConfig', 'parameters', 'yAxisMax']);
  return Boolean(yAxisIncludesZero) && (Boolean(yAxisMin) || Boolean(yAxisMax));
};

/**
 * Creates an axis range style section with min/max fields in a 2-column layout.
 *
 * @param axis - 'x' or 'y'
 * @param options.includesZero - adds a "Include 0" checkbox and warning banner (Scatter/Line y-axis)
 */
export function createAxisRangeStyleSection(
  axis: 'x' | 'y',
  options?: {includesZero?: boolean}
): ShapeUISchema {
  const label = axis === 'x' ? 'X-axis' : 'Y-axis';
  const rangeLabel = axis === 'x' ? 'X-Axis Range' : 'Y-Axis Range';
  const minKey = `${axis}AxisMin`;
  const maxKey = `${axis}AxisMax`;
  const rangeKey = `${axis}AxisRange`;

  const rangeGroup = {
    [FIELD_ORDER_UI_SCHEMA_KEY]: [minKey, maxKey],
    [SECTION_NAME_UI_SCHEMA_KEY]: rangeLabel,
    [NUM_COLUMNS_KEY]: 2,
    ...(options?.includesZero
      ? {
          [BANNER_KEY]: {
            text: 'Including 0 on the y-axis might override the set range',
            bannerType: InlineBannerType.WARNING,
            visibilityFn: axisMinMaxBannerVisibilityFn,
          },
        }
      : {}),
    [minKey]: {
      displayName: 'Min',
      configShapePath: ['chartConfig', 'parameters', minKey],
    },
    [maxKey]: {
      displayName: 'Max',
      configShapePath: ['chartConfig', 'parameters', maxKey],
    },
  };

  if (options?.includesZero) {
    const includesZeroKey = `${axis}AxisIncludesZero`;
    return {
      [SECTION_NAME_UI_SCHEMA_KEY]: label,
      [FIELD_ORDER_UI_SCHEMA_KEY]: [includesZeroKey, rangeKey],
      [includesZeroKey]: {
        [FIELD_ORDER_UI_SCHEMA_KEY]: [includesZeroKey],
        [includesZeroKey]: {
          configShapePath: ['chartConfig', 'parameters', includesZeroKey],
          editorOverride: StepConfigEditorOverride.CHECKBOX,
          fieldSettings: {
            checkboxLabel: 'Include 0',
          },
        },
      },
      [rangeKey]: rangeGroup,
    } as unknown as ShapeUISchema;
  }

  return {
    [SECTION_NAME_UI_SCHEMA_KEY]: label,
    [FIELD_ORDER_UI_SCHEMA_KEY]: [rangeKey],
    [rangeKey]: rangeGroup,
  } as unknown as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// 1e. Error bars section factory
// ---------------------------------------------------------------------------

/**
 * Creates the error bars config section.
 *
 * @param displayName - field label (default: 'Error bar')
 */
export function createErrorBarsSection(displayName = 'Error bar'): ShapeUISchema {
  return {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['errorBarColumnName'],
    errorBarColumnName: {
      displayName,
      configShapePath: ['chartConfig', 'parameters', 'errorBarColumnName'],
      fieldSettings: {
        allowSelectNull: true,
        noColumnSelectedText: 'None',
      },
    },
  } as unknown as ShapeUISchema;
}

// ---------------------------------------------------------------------------
// 1f. Axis config group factory
// ---------------------------------------------------------------------------

const AXIS_SCALE_DISPLAY_NAMES = {
  LINEAR: 'Linear',
  LOG: 'Log',
} as const;

/**
 * Creates an axis config group (columnName + label, optionally scale and point label).
 * Used in the Configure tab for x/y axis selection.
 *
 * @param axis - 'x' or 'y'
 * @param options.includeScale - adds a scale selector (LINEAR/LOG)
 * @param options.includePointLabel - adds a "Label points by" column selector (x-axis only)
 */
export function createAxisConfigGroup(
  axis: 'x' | 'y',
  options?: {includeScale?: boolean; includePointLabel?: boolean}
): ShapeUISchema {
  const columnNameKey = `${axis}AxisColumnName`;
  const labelKey = `${axis}AxisLabel`;
  const scaleKey = `${axis}AxisScale`;
  const displayName = axis === 'x' ? 'X-axis' : 'Y-axis';

  const includeScale = options?.includeScale ?? true;

  const fieldOrder: Array<string> = [columnNameKey, labelKey];
  const detailFields: Array<string> = [labelKey];

  if (options?.includePointLabel) {
    fieldOrder.push('pointLabelColumnName');
    detailFields.push('pointLabelColumnName');
  }
  if (includeScale) {
    fieldOrder.push(scaleKey);
    detailFields.push(scaleKey);
  }

  return {
    [FIELD_ORDER_UI_SCHEMA_KEY]: fieldOrder,
    [EXPANDABLE_DETAILS_KEY]: {
      headerField: columnNameKey,
      detailFields,
    },
    [columnNameKey]: {
      displayName,
      configShapePath: ['chartConfig', 'parameters', columnNameKey],
    },
    [labelKey]: {
      displayName: 'Custom axis label',
      configShapePath: ['chartConfig', 'parameters', labelKey],
    },
    ...(options?.includePointLabel
      ? {
          pointLabelColumnName: {
            displayName: 'Label points by',
            configShapePath: ['chartConfig', 'parameters', 'pointLabelColumnName'],
            infoText: 'Data from this column will be shown when hovering over a point in the chart.',
            fieldSettings: {
              allowSelectNull: true,
              noColumnSelectedText: 'None',
            },
          },
        }
      : {}),
    ...(includeScale
      ? {
          [scaleKey]: {
            displayName: 'Scale',
            configShapePath: ['chartConfig', 'parameters', scaleKey],
            fieldSettings: {
              displayNames: AXIS_SCALE_DISPLAY_NAMES,
            },
          },
        }
      : {}),
  } as unknown as ShapeUISchema;
}
